import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { isInfraAdmin } from "../rbac";
import { readFindTotal } from "shared/truncation";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  isPortainerConnectionValid,
  type PortainerConnectionStatus,
  type RegistryAuthMethod,
  type RegistryConnection,
  type RegistryConnectionFormData,
} from "../infrastructure.types";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import { isSessionEnded } from "stores/sessionGuard";

export type RegistryConnectionStatusFilter = "all" | PortainerConnectionStatus;

/** Page size for the connection read — a real, server-enforced cap (see
 *  {@link RegistryConnectionsStore.isTruncated}). */
export const REGISTRY_CONNECTION_PAGE_SIZE = 200;

/**
 * Normalised (flat) result of the ACR connection `validate` action for the UI
 * alert. Mirrors the nested `validation` object the API returns on the record,
 * flattened for a single banner.
 */
export interface RegistryConnectionValidateResult {
  status: PortainerConnectionStatus;
  message?: string;
  repositoryCount?: number;
  lastValidatedAt?: string | null;
}

/**
 * View store for the Registry Connections (Azure Container Registry) admin view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. Registry connections are read through the registered
 * `registry-connections` ServiceStore (see serviceStores/infrastructure.stores.ts);
 * `connections` is a computed view over its entity cache (never held locally).
 *
 * RBAC: reads are staff (viewer+); writes (create/edit/remove) and the
 * `validate` action are admin-only — gated in-UI via {@link canManage}
 * (isInfraAdmin) and re-enforced by the API on every mutating call.
 *
 * SECRET HANDLING: the ACR credential (admin password or service-principal
 * secret) is write-only. The form captures `password` and submits it on save,
 * but the API never returns it — `RegistryConnection` carries only a
 * `hasCredential` boolean + a mask. The form's `password` field is cleared after
 * every save so it is never repopulated from a record.
 */
class RegistryConnectionsStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  /**
   * `total` from the last find envelope — how many rows the SERVER has, as
   * opposed to how many it sent. Null until a paginated response reports one.
   * A plain number, not an entity, so the "no entities in a ViewStore" rule
   * still holds. See ../findTotal.ts for why this is not read off
   * `ServiceStore.pagination.total`.
   */
  serverTotal: number | null = null;

  /**
   * Failure of a row action (currently: remove). Kept separate from `error`
   * (the load failure shown in the empty grid) so a failed delete is announced
   * even when the grid still has rows. Mirrors ClientInstancesStore's
   * `actionError` / `clearActionError()` pair.
   */
  actionError: string | null = null;

  // Header filter
  searchQuery = "";
  statusFilter: RegistryConnectionStatusFilter = "all";

  // Create/edit form state (shared by the connection form dialog)
  editingConnectionId: string | null = null;
  formData: RegistryConnectionFormData = {};
  saving = false;
  formError: string | null = null;

  // Per-connection "Validate" state
  testingConnectionId: string | null = null;
  lastTestResult: RegistryConnectionValidateResult | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `registry-connections` ServiceStore. */
  private get connectionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("registry-connections") ?? this.rootStore.registryConnectionsStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  /**
   * Whether the current user may manage registry connections (create / edit /
   * remove / validate). Admin-only. The API is the real gate; this only governs
   * visibility. See ../rbac.ts.
   */
  get canManage(): boolean {
    return isInfraAdmin(this.rootStore);
  }

  /**
   * Connection list. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get connections(): RegistryConnection[] {
    return (this.connectionsService?.entities ??
      []) as unknown as RegistryConnection[];
  }

  // ---- Derived data -------------------------------------------------------

  connectionName(id?: string | null): string {
    if (!id) return "";
    return this.connections.find((c) => c.id === id)?.name || "";
  }

  connectionById(id?: string | null): RegistryConnection | null {
    if (!id) return null;
    return this.connections.find((c) => c.id === id) || null;
  }

  get filteredConnections(): RegistryConnection[] {
    let result = this.connections;

    if (this.statusFilter !== "all") {
      if (this.statusFilter === "valid") {
        // "Valid" matches the API's success status ("verified") too.
        result = result.filter((c) => isPortainerConnectionValid(c.status));
      } else if (this.statusFilter === "invalid") {
        // "Invalid" covers both invalid + error.
        result = result.filter(
          (c) => c.status === "invalid" || c.status === "error"
        );
      } else {
        result = result.filter((c) => c.status === this.statusFilter);
      }
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.loginServer?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  get totalCount(): number {
    return this.connections.length;
  }

  /** How many rows survive the header filters — what the table renders. */
  /** True when a search or status filter is set — the only time the empty
   *  table may say "match the current filters". */
  get hasActiveFilter(): boolean {
    return this.searchQuery.trim().length > 0 || this.statusFilter !== "all";
  }

  get filteredCount(): number {
    return this.filteredConnections.length;
  }

  /**
   * True when the server holds more rows than this page fetched, i.e. BOTH the
   * summary cards and the table are short of reality. The fix for a real
   * overflow is paging, not a bigger `$limit` — raising the cap only moves the
   * same silence further out.
   */
  get isTruncated(): boolean {
    return this.serverTotal !== null && this.serverTotal > this.totalCount;
  }

  get validCount(): number {
    // The API writes "verified" on a successful validate; treat it as valid.
    return this.connections.filter((c) => isPortainerConnectionValid(c.status))
      .length;
  }

  get unverifiedCount(): number {
    return this.connections.filter(
      (c) => !c.status || c.status === "unverified"
    ).length;
  }

  get invalidCount(): number {
    return this.connections.filter(
      (c) => c.status === "invalid" || c.status === "error"
    ).length;
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setStatusFilter(filter: RegistryConnectionStatusFilter) {
    this.statusFilter = filter;
  }

  // ---- Loading ------------------------------------------------------------

  async loadConnections() {
    this.loading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `connections` reads.
      // `registry-connections` is a real paginated KnexService, so this
      // `$limit` is a cap the server enforces — capture the envelope's `total`
      // and let the view disclose any shortfall rather than showing a partial
      // registry as if it were the whole one.
      // findOrThrow: the ServiceStore resolves null on a failed find, which
      // rendered the "none yet" empty state and zero tiles.
      const result = await findOrThrow(this.connectionsService, {
        query: { $limit: REGISTRY_CONNECTION_PAGE_SIZE, $sort: { name: 1 } },
      });
      const total = readFindTotal(result);
      runInAction(() => {
        this.serverTotal = total;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load connections");
      });

      console.error("Failed to load registry connections:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.loadConnections();
  }

  /** Ensure the list is populated once. */
  async ensureLoaded() {
    if (this.connections.length === 0 && !this.loading) {
      await this.loadConnections();
    }
  }

  // ---- Create / edit form -------------------------------------------------

  startCreate() {
    this.editingConnectionId = null;
    this.formError = null;
    this.lastTestResult = null;
    // password starts empty and stays write-only.
    this.formData = {
      name: "",
      loginServer: "",
      authMethod: "admin",
      username: "",
      tenantId: "",
      password: "",
    };
  }

  startEdit(connection: RegistryConnection) {
    this.editingConnectionId = connection.id;
    this.formError = null;
    this.lastTestResult = null;
    // IMPORTANT: never seed `password` from a record — the API does not return
    // it. Leaving it blank means "keep the existing credential"; a non-empty
    // value on save rotates it.
    this.formData = {
      name: connection.name,
      loginServer: connection.loginServer,
      authMethod: connection.authMethod,
      username: connection.username ?? "",
      tenantId: connection.tenantId ?? "",
      password: "",
    };
  }

  get isEditing(): boolean {
    return !!this.editingConnectionId;
  }

  setFormField(
    field: keyof RegistryConnectionFormData,
    value: string | null
  ) {
    this.formData = {
      ...this.formData,
      [field]: value,
    } as RegistryConnectionFormData;
  }

  /** True when the current form targets a service-principal (tenantId shown). */
  get isServicePrincipal(): boolean {
    return this.formData.authMethod === "service-principal";
  }

  get isFormValid(): boolean {
    const hasBasics =
      !!this.formData.name?.trim() &&
      !!this.formData.loginServer?.trim() &&
      !!this.formData.authMethod &&
      !!this.formData.username?.trim();
    // A tenant id is only required for the service-principal auth method.
    const hasTenant = this.isServicePrincipal
      ? !!this.formData.tenantId?.trim()
      : true;
    const base = hasBasics && hasTenant;
    // On create a credential is required; on edit a blank one means "unchanged".
    if (this.editingConnectionId) return base;
    return base && !!this.formData.password?.trim();
  }

  /**
   * Persist the create/edit form. The credential (when provided) is sent to the
   * API which encrypts it at rest; it is never echoed back. After saving, the
   * write-only `password` field is cleared.
   */
  async saveConnection(): Promise<boolean> {
    if (!this.isFormValid) {
      this.formError = this.editingConnectionId
        ? this.isServicePrincipal
          ? "Name, Login Server, Client ID and Tenant ID are required."
          : "Name, Login Server and Username are required."
        : this.isServicePrincipal
          ? "Name, Login Server, Client ID, Tenant ID and a Secret are required."
          : "Name, Login Server, Username and a Password are required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    try {
      const authMethod = this.formData.authMethod as RegistryAuthMethod;
      const payload: Record<string, unknown> = {
        name: this.formData.name?.trim(),
        loginServer: this.formData.loginServer?.trim(),
        authMethod,
        username: this.formData.username?.trim(),
        // tenantId only applies to service-principal; clear it otherwise so a
        // method switch does not leave a stale tenant behind.
        tenantId:
          authMethod === "service-principal"
            ? this.formData.tenantId?.trim() || null
            : null,
      };
      // Only include the credential when the user actually entered one. On edit
      // an empty value keeps the existing encrypted credential untouched.
      if (this.formData.password?.trim()) {
        payload.password = this.formData.password.trim();
      }

      if (this.editingConnectionId) {
        await this.connectionsService?.patch(this.editingConnectionId, payload, THROW_ON_ERROR);
      } else {
        await this.connectionsService?.create(payload as never, THROW_ON_ERROR);
      }

      await this.reload();
      runInAction(() => {
        this.editingConnectionId = null;
        // Clear the write-only credential so it never lingers in memory/state.
        this.formData = {};
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            err instanceof Error ? err.message : "Failed to save connection";
        }
      });

      console.error("saveConnection failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** Dismiss the row-action error banner. */
  clearActionError() {
    this.actionError = null;
  }

  /**
   * Delete a registry connection (and, server-side, its encrypted ACR
   * credential). Destructive and irreversible: the caller MUST confirm first
   * (the grid uses `ConfirmingIconButton`). Failures land on `actionError`
   * rather than only the console.
   */
  async removeConnection(id: string) {
    this.actionError = null;
    try {
      await this.connectionsService?.remove(id, THROW_ON_ERROR);
      await this.reload();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.actionError =
            err instanceof Error ? err.message : "Failed to remove connection";
        }
      });
      console.error("Failed to remove connection:", err);
    }
  }

  /**
   * Trigger the API's `validate` custom action for a saved connection. The API
   * connects to ACR using the encrypted credential and reports usability
   * (repository count on success). No secret is sent from the client.
   */
  async testConnection(id: string): Promise<void> {
    this.testingConnectionId = id;
    this.lastTestResult = null;
    try {
      // The API exposes connection validation as a custom action via `create`
      // (POST) with `{ action: 'validate', id }`. It returns the updated record
      // (top-level `status`/`lastValidatedAt`) with a nested `validation` object.
      const response = (await this.connectionsService?.create({
        action: "validate",
        id,
      } as never)) as unknown as RegistryConnection;
      runInAction(() => {
        this.lastTestResult = {
          // The validation status is the authoritative outcome ("verified"/…).
          status: response.validation?.status ?? response.status ?? "unverified",
          message: response.validation?.message,
          repositoryCount: response.validation?.repositoryCount,
          lastValidatedAt: response.lastValidatedAt ?? null,
        };
      });
      // Reload so the freshly-computed status/lastValidatedAt show in the list.
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.lastTestResult = {
            status: "error",
            message:
              err instanceof Error ? err.message : "Connection test failed",
          };
        }
      });

      console.error("testConnection failed:", err);
    } finally {
      runInAction(() => {
        this.testingConnectionId = null;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.serverTotal = null;
    this.actionError = null;
    this.searchQuery = "";
    this.statusFilter = "all";
    this.editingConnectionId = null;
    this.formData = {};
    this.saving = false;
    this.formError = null;
    this.testingConnectionId = null;
    this.lastTestResult = null;
  }
}

export default RegistryConnectionsStore;
